import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Play, X, ExternalLink, Video } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export const releaseVideo = (release) => {
  const v = release?.video;
  if (!v || !v.visible) return null;
  const yt = (v.youtube_url || "").trim();
  if (!v.primary && !v.widescreen && !yt) return null;
  return { ...v, youtube_url: yt, poster: v.thumbnail_url || v.primary?.poster_url || v.widescreen?.poster_url || release.artwork_url };
};

const isVertical = (slot) => (slot?.aspect || "16:9") === "9:16";
const ytLabel = (url) => (/vimeo\.com/.test(url) ? "Watch on Vimeo" : "Watch on YouTube");

const Player = ({ slot, poster, title, autoPlay }) => {
  const vertical = isVertical(slot);
  const ref = useRef();
  useEffect(() => { if (autoPlay) ref.current?.play().catch(() => {}); }, [autoPlay, slot?.url]);
  return (
    <div className="mx-auto" style={vertical
      ? { aspectRatio: "9 / 16", height: "min(64vh, 760px)", maxWidth: "100%" }
      : { aspectRatio: "16 / 9", width: "min(92vw, 1100px, 112vh)" }}
      data-testid={`video-player-${vertical ? "vertical" : "wide"}`}>
      <video key={slot.url} ref={ref} src={mediaUrl(slot.url)} poster={poster ? mediaUrl(poster) : undefined} controls playsInline preload="metadata"
             className="w-full h-full object-contain bg-black" style={{ borderRadius: "var(--radius)" }} title={title}
             onPlay={() => window.dispatchEvent(new CustomEvent("hns:video-play"))} />
    </div>
  );
};

export const VideoModal = ({ release, onClose }) => {
  const v = useMemo(() => releaseVideo(release), [release]);
  const both = !!(v?.primary && v?.widescreen);
  const desktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
  const [cut, setCut] = useState(() => {
    if (both) return (desktop ? (isVertical(v.widescreen) ? "primary" : "widescreen") : (isVertical(v.primary) ? "primary" : "widescreen"));
    return v?.primary ? "primary" : "widescreen";
  });
  const [watching, setWatching] = useState(false);
  const slot = v?.[cut] || v?.primary || v?.widescreen;
  const hasFile = !!slot;
  const title = v?.title || `${release.title} — Official Video`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  if (!v) return null;
  return createPortal(
    <div className="fixed inset-0 z-[90] grid place-items-center px-4 py-6" style={{ background: "rgba(11,9,8,.9)", backdropFilter: "blur(14px)" }}
         onClick={onClose} role="dialog" aria-modal="true" aria-label={title} data-testid={`video-modal-${release.id}`}>
      <div className="frame p-4 sm:p-6 w-full max-h-[94vh] overflow-y-auto" style={{ backgroundColor: "var(--ink-raised)", maxWidth: hasFile && isVertical(slot) && !both ? 560 : 1180 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-caps text-gold" style={{ fontSize: ".62rem" }}>Music video</p>
            <h3 className="font-display foil-text mt-1" style={{ fontSize: "clamp(1.3rem, 2.4vw, 1.8rem)", lineHeight: 1.1 }}>{title}</h3>
            <p className="mt-1" style={{ color: "var(--ash)", fontSize: ".8rem" }}>{release.credit || "P. Frazy · Heart N Soul Records"}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gold shrink-0" style={{ background: "none", border: "1px solid rgba(200,155,60,.4)", borderRadius: "var(--radius)", padding: 8 }} data-testid="video-modal-close"><X size={16} /></button>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="video-options">
          {hasFile && (
            <button className="btn-gold" onClick={() => setWatching(true)} data-testid="video-watch-here">
              <Play size={14} fill="currentColor" /> Watch here
            </button>
          )}
          {v.youtube_url && (
            <a className={hasFile ? "btn-ghost" : "btn-gold"} href={v.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="video-watch-youtube">
              <ExternalLink size={14} /> {ytLabel(v.youtube_url)}
            </a>
          )}
          {both && (
            <div className="ml-auto inline-flex font-caps" style={{ fontSize: ".58rem", border: "1px solid rgba(200,155,60,.4)", borderRadius: "var(--radius)", overflow: "hidden" }} data-testid="video-cut-toggle">
              {["primary", "widescreen"].map((k) => {
                const label = isVertical(v[k]) ? "Vertical" : "Widescreen";
                const active = cut === k;
                return (
                  <button key={k} onClick={() => setCut(k)} data-testid={`video-cut-${label.toLowerCase()}`}
                          style={{ padding: ".55rem .9rem", background: active ? "var(--gold)" : "transparent", color: active ? "#000" : "var(--gold)", border: "none", cursor: "pointer", letterSpacing: ".16em", fontWeight: 700 }}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {hasFile && (
          <div className="mt-5">
            {watching ? (
              <Player slot={slot} poster={v.poster} title={title} autoPlay />
            ) : (
              <button onClick={() => setWatching(true)} className="group relative block mx-auto overflow-hidden" aria-label={`Play ${title}`} data-testid="video-poster-play"
                      style={isVertical(slot) ? { aspectRatio: "9 / 16", height: "min(64vh, 760px)", borderRadius: "var(--radius)", background: "#000" } : { aspectRatio: "16 / 9", width: "min(92vw, 1100px, 112vh)", borderRadius: "var(--radius)", background: "#000" }}>
                {v.poster && <img src={mediaUrl(v.poster)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />}
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid place-items-center transition-transform group-hover:scale-110" style={{ width: 84, height: 84, borderRadius: "50%", border: "1px solid var(--gold)", background: "rgba(11,9,8,.55)", color: "var(--gold)" }}>
                    <Play size={30} fill="currentColor" />
                  </span>
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export const WatchVideoButton = ({ release, className = "btn-gold", style, autoOpen = false }) => {
  const [open, setOpen] = useState(autoOpen);
  const v = releaseVideo(release);
  if (!v) return null;
  return (
    <>
      <button className={className} style={style} onClick={() => setOpen(true)} data-testid={`watch-video-btn-${release.id}`}>
        <Video size={14} /> Watch the video
      </button>
      {open && <VideoModal release={release} onClose={() => setOpen(false)} />}
    </>
  );
};
